import * as d3 from 'd3';
import { getChartChrome } from '../../../../../theme/chartChrome';
import { getRowerColorByKey } from '../../../../../utils/rowerColors';
import {
  formatTimeLabel,
  filterVisibleStrokes,
  calculateResponsiveFontSizes,
  type PowerDataPoint,
} from '../utils';
import type { RenderConfig } from './types';
import { setupZoomBehavior } from './zoom';
import { setupBrush } from './brush';
import { setupTooltip } from './tooltip';

export class PowerAnalysisChartRenderer {
  private cleanupFunctions: Array<() => void> = [];
  private static readonly ACTIVE_REGION_RATIO = 0.7;

  render(config: RenderConfig): void {
    const {
      svgElement,
      containerElement,
      tooltipElement,
      loading,
      hasData,
      fontsReady,
      width,
      height,
      correctedData,
      visibleRowers,
      activeRowerKeys,
      sessionId,
      rowerContext: _rowerContext,
      focusedRowerKey,
      windowStart,
      windowEnd,
      windowDuration: _windowDuration,
      dataMode: _dataMode,
      strokeBounds: _strokeBounds,
      hiddenRowerSet: _hiddenRowerSet,
      handleLegendToggle: _handleLegendToggle,
      currentTime,
      timeExtent,
      effectiveTimeMin: _effectiveTimeMin,
      animationRange,
      isPlaying,
      allStrokeCycles,
      responsiveFontSizes: _responsiveFontSizesFromReact,
      perStrokeMode,
      selectedStrokeIndex: _selectedStrokeIndex,
      activeTab: _activeTab,
      prevDimensionsRef,
      zoomRef,
      zoomTransformRef,
      onZoomToWindow,
      useLockedView = false,
    } = config;

    if (!svgElement || loading || !hasData || !fontsReady) {
      return;
    }

    // Measure the SVG element itself (not the container) to get its actual rendered size
    // This ensures viewBox matches the SVG's CSS size
    const svgRect = svgElement.getBoundingClientRect();
    let actualWidth = svgRect.width > 0 ? svgRect.width : width;
    let actualHeight = svgRect.height > 0 ? svgRect.height : height;

    // Fallback to container if SVG not yet rendered
    if ((actualWidth === width || actualHeight === height) && containerElement) {
      const containerRect = containerElement.getBoundingClientRect();
      if (containerRect.width > 0 && containerRect.height > 0) {
        actualWidth = containerRect.width;
        actualHeight = containerRect.height;
      }
    }

    const wasHidden =
      prevDimensionsRef.current.width === 0 || prevDimensionsRef.current.height === 0;
    const isNowVisible = actualWidth > 0 && actualHeight > 0;

    if (wasHidden && isNowVisible && svgElement) {
      const svg = d3.select(svgElement);
      svg.selectAll('*').remove();
    }

    prevDimensionsRef.current = { width: actualWidth, height: actualHeight };

    if (!actualWidth || !actualHeight) return;

    const margin = { top: 40, right: 80, bottom: 50, left: 80 };
    const plotWidth = actualWidth - margin.left - margin.right;
    const plotHeight = actualHeight - margin.top - margin.bottom;

    // Calculate font sizes from actualWidth (same dimension used for viewBox)
    // This ensures fonts and viewBox coordinate system stay in sync
    const calculatedFontSizes = calculateResponsiveFontSizes(actualWidth);

    const svg = d3.select(svgElement);
    svg.selectAll('*').remove();

    const sessionDuration = Math.round(timeExtent[1] - timeExtent[0]);
    const rowerCount = visibleRowers.length;
    svg
      .attr('viewBox', `0 0 ${actualWidth} ${actualHeight}`)
      .attr('preserveAspectRatio', 'none')
      .attr('role', 'img')
      .attr(
        'aria-label',
        `Power analysis chart showing ${rowerCount} rower${rowerCount !== 1 ? 's' : ''} over ${sessionDuration} seconds. Interactive chart with zoom and pan capabilities.`
      )
      .style('font-family', 'Inter, sans-serif');

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', `plot-clip-${sessionId}`)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', plotWidth)
      .attr('height', plotHeight);

    g.append('defs')
      .append('clipPath')
      .attr('id', `reveal-clip-${sessionId}`)
      .attr('clipPathUnits', 'userSpaceOnUse')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', plotWidth * PowerAnalysisChartRenderer.ACTIVE_REGION_RATIO)
      .attr('height', plotHeight);

    const plotGroup = g
      .append('g')
      .attr('class', 'plot-content')
      .attr('clip-path', `url(#plot-clip-${sessionId})`);

    const chartData = correctedData;
    const renderRowerKeys = activeRowerKeys.length > 0 ? activeRowerKeys : visibleRowers;

    const xScale = d3.scaleLinear().domain([windowStart, windowEnd]).range([0, plotWidth]);

    const windowValues = chartData.flatMap((d) =>
      renderRowerKeys
        .map((key) => (d as PowerDataPoint)[key] as number)
        .filter((v) => v != null && isFinite(v))
    );
    const yExtent = d3.extent(windowValues.length ? windowValues : [0, 100]) as [number, number];
    const yPadding = (yExtent[1] - yExtent[0]) * 0.1;
    const yScale = d3
      .scaleLinear()
      .domain([Math.max(0, yExtent[0] - yPadding), yExtent[1] + yPadding])
      .range([plotHeight, 0])
      .nice();

    const chrome = getChartChrome();

    plotGroup
      .append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(xScale.ticks(10))
      .enter()
      .append('line')
      .attr('x1', (d) => xScale(d))
      .attr('x2', (d) => xScale(d))
      .attr('y1', 0)
      .attr('y2', plotHeight)
      .attr('stroke', chrome.grid)
      .attr('stroke-width', 1);

    plotGroup
      .append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(yScale.ticks(8))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', plotWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', chrome.grid)
      .attr('stroke-width', 1);

    if (allStrokeCycles.length > 0) {
      const visibleStrokes = filterVisibleStrokes(allStrokeCycles, windowStart, windowEnd);

      if (visibleStrokes.length > 0) {
        const strokeCyclesGroup = plotGroup.append('g').attr('class', 'stroke-cycles');

        strokeCyclesGroup
          .selectAll('rect')
          .data(visibleStrokes)
          .enter()
          .append('rect')
          .attr('x', (d) => {
            const x = xScale(d.start);
            return x;
          })
          .attr('width', (d) => {
            const width = xScale(d.end) - xScale(d.start);
            return Math.max(1, width);
          })
          .attr('y', 0)
          .attr('height', plotHeight)
          .attr('fill', '#3B82F6')
          .attr('opacity', 0.12)
          .attr('stroke', '#3B82F6')
          .attr('stroke-width', 0.5)
          .attr('stroke-opacity', 0.2)
          .attr(
            'clip-path',
            isPlaying && !perStrokeMode ? `url(#reveal-clip-${sessionId})` : 'none'
          )
          .style('pointer-events', 'none');

        const labelsData = visibleStrokes.map((stroke) => ({
          ...stroke,
          strokeNumber: stroke.strokeNumber || 0,
        }));

        strokeCyclesGroup
          .selectAll('text.stroke-number')
          .data(labelsData, (d: any) => `stroke-${d.strokeNumber}-${d.start}-${d.end}`)
          .enter()
          .append('text')
          .attr('class', 'stroke-number')
          .attr('x', (d) => {
            const centerX = xScale(d.start) + (xScale(d.end) - xScale(d.start)) / 2;
            return centerX;
          })
          .attr('y', 45)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'baseline')
          .attr('fill', chrome.axisTick)
          .attr('font-size', calculatedFontSizes.strokeNumber)
          .attr('font-weight', 'bold')
          .attr('font-family', 'system-ui, -apple-system, sans-serif')
          .attr(
            'clip-path',
            isPlaying && !perStrokeMode ? `url(#reveal-clip-${sessionId})` : 'none'
          )
          .style('pointer-events', 'none')
          .text((d) => d.strokeNumber);
      }
    }

    // Scale tick count to available width so labels never overlap (~90px per tick)
    const xTickCount = Math.max(3, Math.min(10, Math.floor(plotWidth / 90)));
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(xTickCount)
      .tickFormat(formatTimeLabel(xScale) as any);

    const yAxis = d3
      .axisLeft(yScale)
      .ticks(8)
      .tickFormat((d) => `${d}`);

    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(xAxis)
      .call((g) =>
        g
          .selectAll('text')
          .attr('font-size', calculatedFontSizes.axisTick)
          .attr('fill', chrome.axisTick)
          .attr('dy', '0.71em')
          .style('pointer-events', 'none')
      )
      .call((g) => g.selectAll('line').attr('stroke', chrome.axisLine))
      .call((g) => g.select('.domain').attr('stroke', chrome.axisLine));

    g.append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .call((g) =>
        g
          .selectAll('text')
          .attr('font-size', calculatedFontSizes.axisTick)
          .attr('font-family', 'Inter, sans-serif')
          .attr('fill', chrome.axisTick)
          .attr('dx', '-0.5em')
          .style('pointer-events', 'none')
      )
      .call((g) => g.selectAll('line').attr('stroke', chrome.axisLine))
      .call((g) => g.select('.domain').attr('stroke', chrome.axisLine));

    svg
      .append('text')
      .attr('class', 'x-axis-label')
      .attr('x', actualWidth / 2)
      .attr('y', actualHeight - 10)
      .attr('text-anchor', 'middle')
      .attr('font-size', calculatedFontSizes.axisLabel)
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-weight', 500)
      .attr('fill', chrome.axisLabel)
      .text('Time (seconds)');

    svg
      .append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', `rotate(-90)`)
      .attr('x', -actualHeight / 2)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', calculatedFontSizes.axisLabel)
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-weight', 500)
      .attr('fill', chrome.axisLabel)
      .text('Power (watts)');

    const line = d3
      .line<PowerDataPoint>()
      .defined((d) => d != null && isFinite((d as PowerDataPoint).time))
      .x((d) => xScale((d as PowerDataPoint).time))
      .curve(d3.curveCatmullRom.alpha(0.5));

    renderRowerKeys.forEach((rowerKey, index) => {
      const rowerData = chartData.filter(
        (d) =>
          (d as PowerDataPoint)[rowerKey] != null &&
          isFinite((d as PowerDataPoint)[rowerKey] as number)
      );

      const linePath = line.y((d) => yScale((d as PowerDataPoint)[rowerKey] as number));

      const isFocused = focusedRowerKey === rowerKey;
      const isNonFocused = focusedRowerKey !== null && !isFocused;

      const strokeColor = isNonFocused ? 'rgba(100, 100, 100, 0.25)' : getRowerColorByKey(rowerKey);

      const lineOpacity = isNonFocused ? 0.25 : focusedRowerKey ? 1.0 : 0.85;

      if (!isNonFocused) {
        plotGroup
          .append('path')
          .datum(rowerData)
          .attr('class', `ghost-line-${index}`)
          .attr('fill', 'none')
          .attr('stroke', getRowerColorByKey(rowerKey))
          .attr('stroke-width', 1)
          .attr('opacity', 0.15)
          .attr('d', linePath);
      }

      const shouldClip = isPlaying && !perStrokeMode;
      plotGroup
        .append('path')
        .datum(rowerData)
        .attr('class', `active-line-${index}`)
        .attr('fill', 'none')
        .attr('stroke', strokeColor)
        .attr('stroke-width', isNonFocused ? 2 : 2.5)
        .attr('opacity', lineOpacity)
        .attr('clip-path', shouldClip ? `url(#reveal-clip-${sessionId})` : 'none')
        .attr('d', linePath);
    });

    const indicatorX = plotWidth * PowerAnalysisChartRenderer.ACTIVE_REGION_RATIO;
    const showIndicator = isPlaying && !perStrokeMode;
    plotGroup
      .append('line')
      .attr('class', 'current-time-indicator')
      .attr('x1', indicatorX)
      .attr('x2', indicatorX)
      .attr('y1', 0)
      .attr('y2', plotHeight)
      .attr('stroke', '#EF4444')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4 4')
      .style('opacity', showIndicator ? 1 : 0)
      .style('pointer-events', 'none');

    plotGroup
      .append('text')
      .attr('class', 'current-time-label')
      .attr('x', indicatorX)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .attr('fill', '#EF4444')
      .style('opacity', isPlaying ? 1 : 0)
      .style('pointer-events', 'none')
      .text(`${Math.round(currentTime)}s`);

    // Legend is rendered as React HTML in PowerAnalysisD3.tsx (outside the SVG)
    // so it is never torn down by D3 re-renders and stays clickable during playback.

    const crosshair = g
      .append('line')
      .attr('class', 'crosshair')
      .attr('y1', 0)
      .attr('y2', plotHeight)
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('opacity', 0)
      .style('pointer-events', 'none');

    // When view is driven by state, clear the zoom transform so the scale domain is the true view.
    // While playing, do NOT clear so zoom-during-play is not wiped each frame (avoids glitch).
    if (useLockedView && !isPlaying) {
      zoomTransformRef.current = d3.zoomIdentity;
      svg.property('__zoom', d3.zoomIdentity);
    }

    const { behavior: zoomBehavior } = setupZoomBehavior({
      svg,
      g,
      plotGroup,
      xScale,
      yScale,
      yAxis,
      plotWidth,
      plotHeight,
      windowStart,
      windowEnd,
      timeExtent,
      animationRange,
      isPlaying,
      perStrokeMode,
      sessionId,
      allStrokeCycles,
      renderRowerKeys,
      // Use sizes derived from measured SVG width — not React’s fallback (e.g. 800px) — so zoom
      // overlays match the main chart and fonts don’t look stretched after load/resize.
      responsiveFontSizes: calculatedFontSizes,
      zoomRef,
      zoomTransformRef,
      onZoomToWindow,
      useLockedView,
    });

    const tooltipCleanup = setupTooltip({
      g,
      tooltipElement,
      crosshair,
      xScale,
      plotWidth,
      plotHeight,
      chartData,
      renderRowerKeys,
      isPlaying,
      perStrokeMode,
      margin,
      actualWidth,
      actualHeight,
      zoomBehavior,
    });

    const brushCleanup = setupBrush({
      svg,
      g,
      plotWidth,
      plotHeight,
      sessionId,
      xScale,
      zoomBehavior,
      zoomTransformRef,
      onZoomToWindow,
      onForceReRender: config.onForceReRender,
    });

    this.cleanupFunctions.push(() => {
      tooltipCleanup.cleanup();
      brushCleanup.cleanup();
      svg.selectAll('*').remove();
    });
  }

  cleanup(): void {
    this.cleanupFunctions.forEach((fn) => fn());
    this.cleanupFunctions = [];
  }
}

export type { RenderConfig } from './types';
